<template>
  <v-dialog v-model="dialog" max-width="1320px" persistent>
    <div class="home-wage-retirement-compare-modal">
      <header class="home-wage-retirement-compare-modal__header">
        <div class="home-wage-retirement-compare-modal__title-wrap">
          <h2 class="home-wage-retirement-compare-modal__title">퇴직금 비교 (고객 vs 카이온)</h2>
          <span class="admin-only-chip">최고관리자</span>
        </div>
        <button
          type="button"
          class="home-wage-retirement-compare-modal__close"
          aria-label="닫기"
          @click="close"
        >
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </header>

      <p class="home-wage-retirement-compare-modal__desc">
        출력할 사람을 체크하고 ↑↓로 순서를 바꾼 뒤 「선택 출력」하면, 체크된 사람만 현재 순서대로 인쇄됩니다.
        월 60시간 미만은 퇴직금 대상이 아니므로 「-」로 표시합니다.
      </p>

      <div class="home-wage-retirement-compare-modal__toolbar">
        <div class="home-wage-retirement-compare-modal__toolbar-left">
          <button type="button" class="rc-tool-btn" @click="selectAll">전체선택</button>
          <button type="button" class="rc-tool-btn" @click="selectWithRetirement">퇴직금 있는 사람만</button>
          <button type="button" class="rc-tool-btn" @click="clearSelection">선택해제</button>
          <span class="rc-tool-count">선택 {{ selectedCount }} / {{ rows.length }}명</span>
        </div>
        <div class="home-wage-retirement-compare-modal__toolbar-right">
          <button
            type="button"
            class="rc-tool-btn rc-tool-btn--primary"
            :disabled="selectedCount === 0"
            @click="printSelected"
          >
            <v-icon size="14">mdi-printer</v-icon>
            선택 출력
          </button>
        </div>
      </div>

      <div class="home-wage-retirement-compare-modal__summary">
        <span>{{ yearLabel }} · 전체 {{ rows.length }}명</span>
        <span>선택 고객합계 {{ formatMoney(sumSelectedCustomer) }}원</span>
        <span>선택 카이온합계 {{ formatMoney(sumSelectedCaion) }}원</span>
        <span>선택 차이 {{ formatMoney(sumSelectedCustomer - sumSelectedCaion) }}원</span>
      </div>

      <div class="home-wage-retirement-compare-modal__table-wrap">
        <table class="home-wage-retirement-compare-modal__table">
          <thead>
            <tr>
              <th class="col-check">
                <input
                  type="checkbox"
                  :checked="allSelected"
                  title="전체선택"
                  @change="onToggleSelectAll"
                >
              </th>
              <th class="col-order">순서</th>
              <th class="col-seq">순번</th>
              <th class="col-name">이름</th>
              <th class="col-hours">근무시간</th>
              <th class="col-pay">급여</th>
              <th class="col-amt">고객계산퇴직금</th>
              <th class="col-amt">카이온계산퇴직금</th>
              <th class="col-note">비고</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="rows.length === 0">
              <td colspan="9" class="col-empty">비교할 임금 데이터가 없습니다.</td>
            </tr>
            <tr
              v-for="(row, idx) in rows"
              :key="row.rowKey"
              :class="{
                'is-diff': row.retirementEligible && row.diffAmount !== 0,
                'is-match': row.retirementEligible && row.diffAmount === 0,
                'is-under60': !row.retirementEligible,
                'is-checked': row.checked,
              }"
            >
              <td class="col-check">
                <input v-model="row.checked" type="checkbox">
              </td>
              <td class="col-order">
                <button
                  type="button"
                  class="rc-order-btn"
                  :disabled="idx === 0"
                  title="위로"
                  @click="moveRow(idx, -1)"
                >
                  <v-icon size="16">mdi-chevron-up</v-icon>
                </button>
                <button
                  type="button"
                  class="rc-order-btn"
                  :disabled="idx === rows.length - 1"
                  title="아래로"
                  @click="moveRow(idx, 1)"
                >
                  <v-icon size="16">mdi-chevron-down</v-icon>
                </button>
              </td>
              <td class="col-seq">{{ idx + 1 }}</td>
              <td class="col-name">{{ row.name }}</td>
              <td class="col-hours">{{ formatHours(row.workHours) }}</td>
              <td class="col-pay">{{ formatMoney(row.grossPay) }}</td>
              <td class="col-amt">{{ formatRetirement(row.customerRetirement, row.retirementEligible) }}</td>
              <td class="col-amt">{{ formatRetirement(row.caionRetirement, row.retirementEligible) }}</td>
              <td
                class="col-note"
                :class="{
                  'col-note--match': row.retirementEligible && row.diffAmount === 0,
                  'col-note--diff': row.retirementEligible && row.diffAmount !== 0,
                  'col-note--under60': !row.retirementEligible,
                }"
              >{{ row.note }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <footer class="home-wage-retirement-compare-modal__footer">
        <v-btn
          variant="flat"
          class="btn-print"
          :disabled="selectedCount === 0"
          @click="printSelected"
        >
          선택 출력
        </v-btn>
        <v-btn variant="flat" class="btn-close" @click="close">닫기</v-btn>
      </footer>
    </div>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue';
import {
  buildHomeWageRetirementCompareRow,
  type HomeWageRetirementCompareRow,
} from '@/policies/homeWageSettingPolicy';

type CompareListRow = HomeWageRetirementCompareRow & {
  rowKey: string;
  checked: boolean;
};

const dialog = ref(false);
const yearMonth = ref('');
const memberName = ref('');
const rows = ref<CompareListRow[]>([]);
let printFrame: HTMLIFrameElement | null = null;

const yearLabel = computed(() => {
  const ym = yearMonth.value.replace(/\D/g, '').slice(0, 6);
  if (ym.length !== 6) return yearMonth.value || '-';
  return `${ym.slice(0, 4)}년 ${Number(ym.slice(4, 6))}월`;
});

const selectedRows = computed(() => rows.value.filter((r) => r.checked));
const selectedCount = computed(() => selectedRows.value.length);
const allSelected = computed(
  () => rows.value.length > 0 && rows.value.every((r) => r.checked)
);

const sumSelectedCustomer = computed(() =>
  selectedRows.value.reduce(
    (s, r) => s + (r.retirementEligible ? Number(r.customerRetirement) || 0 : 0),
    0
  )
);
const sumSelectedCaion = computed(() =>
  selectedRows.value.reduce(
    (s, r) => s + (r.retirementEligible ? Number(r.caionRetirement) || 0 : 0),
    0
  )
);

const formatMoney = (n: number) => (Number(n) || 0).toLocaleString('ko-KR');
const formatHours = (h: number) => {
  const n = Number(h) || 0;
  if (n <= 0) return '-';
  return Number.isInteger(n) ? `${n}` : String(Math.round(n * 10) / 10);
};
const formatRetirement = (amount: number, eligible: boolean) =>
  (eligible ? formatMoney(amount) : '-');

const selectAll = () => {
  rows.value.forEach((r) => {
    r.checked = true;
  });
};

/** 60h 이상 · 고객/카이온 퇴직금이 1원 이상인 사람만 선택 */
const selectWithRetirement = () => {
  rows.value.forEach((r) => {
    r.checked =
      r.retirementEligible
      && ((Number(r.customerRetirement) || 0) > 0 || (Number(r.caionRetirement) || 0) > 0);
  });
};

const clearSelection = () => {
  rows.value.forEach((r) => {
    r.checked = false;
  });
};

const toggleSelectAll = (checked: boolean) => {
  rows.value.forEach((r) => {
    r.checked = checked;
  });
};

const onToggleSelectAll = (event: Event) => {
  const target = event.target as HTMLInputElement | null;
  toggleSelectAll(!!target?.checked);
};

const moveRow = (index: number, delta: number) => {
  const next = index + delta;
  if (next < 0 || next >= rows.value.length) return;
  const list = [...rows.value];
  const [item] = list.splice(index, 1);
  list.splice(next, 0, item);
  rows.value = list.map((r, i) => ({ ...r, seq: i + 1 }));
};

const escapeHtml = (value: string) =>
  String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const removePrintFrame = () => {
  if (printFrame?.parentNode) {
    printFrame.parentNode.removeChild(printFrame);
  }
  printFrame = null;
};

const printSelected = () => {
  const list = selectedRows.value;
  if (list.length === 0) {
    alert('출력할 사람을 체크해 주세요.');
    return;
  }

  const facility = memberName.value || '시설';
  const title = `${facility} ${yearLabel.value} 퇴직금 비교`;
  const bodyRows = list
    .map((row, idx) => {
      const noteClass = !row.retirementEligible
        ? 'under60'
        : row.diffAmount === 0
          ? 'match'
          : 'diff';
      return `
      <tr class="${noteClass}">
        <td class="c">${idx + 1}</td>
        <td>${escapeHtml(row.name)}</td>
        <td class="r">${escapeHtml(formatHours(row.workHours))}</td>
        <td class="r">${formatMoney(row.grossPay)}</td>
        <td class="r">${escapeHtml(formatRetirement(row.customerRetirement, row.retirementEligible))}</td>
        <td class="r">${escapeHtml(formatRetirement(row.caionRetirement, row.retirementEligible))}</td>
        <td class="note">${escapeHtml(row.note)}</td>
      </tr>`;
    })
    .join('');

  const sumCustomer = list.reduce(
    (s, r) => s + (r.retirementEligible ? Number(r.customerRetirement) || 0 : 0),
    0
  );
  const sumCaion = list.reduce(
    (s, r) => s + (r.retirementEligible ? Number(r.caionRetirement) || 0 : 0),
    0
  );

  const html = `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="utf-8">
  <title>${escapeHtml(title)}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 24px; font-family: "Malgun Gothic", "Apple SD Gothic Neo", sans-serif; color: #1e293b; }
    h1 { margin: 0 0 6px; font-size: 18px; }
    .meta { margin: 0 0 14px; font-size: 12px; color: #64748b; }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th, td { border: 1px solid #cbd5e1; padding: 6px 8px; }
    th { background: #f1f5f9; text-align: left; }
    td.c { text-align: center; width: 40px; }
    td.r { text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
    tr.diff { background: #fffbeb; }
    td.note { font-weight: 600; }
    tr.match td.note { color: #15803d; }
    tr.diff td.note { color: #b45309; }
    tr.under60 td.note { color: #64748b; font-weight: 500; }
    tfoot td { font-weight: 700; background: #f8fafc; }
    @media print {
      body { margin: 12px; }
    }
  </style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <p class="meta">출력 ${list.length}명 · 고객합계 ${formatMoney(sumCustomer)}원 · 카이온합계 ${formatMoney(sumCaion)}원 · 차이 ${formatMoney(sumCustomer - sumCaion)}원</p>
  <table>
    <thead>
      <tr>
        <th>순번</th>
        <th>이름</th>
        <th>근무시간</th>
        <th>급여</th>
        <th>고객계산퇴직금</th>
        <th>카이온계산퇴직금</th>
        <th>비고</th>
      </tr>
    </thead>
    <tbody>${bodyRows}</tbody>
    <tfoot>
      <tr>
        <td colspan="4">합계</td>
        <td class="r">${formatMoney(sumCustomer)}</td>
        <td class="r">${formatMoney(sumCaion)}</td>
        <td></td>
      </tr>
    </tfoot>
  </table>
</body>
</html>`;

  removePrintFrame();
  const iframe = document.createElement('iframe');
  iframe.setAttribute('title', '퇴직금 비교 출력');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  document.body.appendChild(iframe);
  printFrame = iframe;

  const frameWindow = iframe.contentWindow;
  const frameDoc = iframe.contentDocument || frameWindow?.document;
  if (!frameWindow || !frameDoc) {
    removePrintFrame();
    alert('출력 준비에 실패했습니다. 잠시 후 다시 시도해 주세요.');
    return;
  }

  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  const runPrint = () => {
    try {
      frameWindow.focus();
      frameWindow.print();
    } catch (error) {
      console.error('퇴직금 비교 출력 오류:', error);
      alert('출력 중 오류가 발생했습니다.');
    } finally {
      window.setTimeout(removePrintFrame, 1000);
    }
  };

  // 브라우저별 문서 로드 타이밍 보정
  window.setTimeout(runPrint, 150);
};

const open = (payload: {
  yearMonth: string;
  wageRows: Record<string, unknown>[];
  memberName?: string;
}) => {
  yearMonth.value = payload.yearMonth || '';
  memberName.value = String(payload.memberName || '').trim();
  const list = Array.isArray(payload.wageRows) ? payload.wageRows : [];
  rows.value = list
    .map((row, idx) => buildHomeWageRetirementCompareRow(row, idx + 1, yearMonth.value))
    .map((row, idx) => ({
      ...row,
      seq: idx + 1,
      rowKey: `${row.name}|${row.workHours}|${row.grossPay}|${idx}`,
      checked: true,
    }));
  dialog.value = true;
};

const close = () => {
  dialog.value = false;
};

onBeforeUnmount(() => {
  removePrintFrame();
});

defineExpose({ open, close });
</script>
